import {
    both,
    propEq,
    compose,
    not,
} from 'ramda';
import Messages from '../messages';

export { defaultComponentOfType } from './defaultComponent';

export const CAMPAIGN_A = 1;
export const CAMPAIGN_B = 2;
export const CAMPAIGN_C = 3;
export const CAMPAIGN_D = 4;
export const CAMPAIGN_SWIPER = 10;
export const CAMPAIGN_INTERSTITIAL = 13;
export const CAMPAIGN_IDS = [
    CAMPAIGN_A,
    CAMPAIGN_B,
    CAMPAIGN_C,
    CAMPAIGN_D,
    CAMPAIGN_SWIPER,
    CAMPAIGN_INTERSTITIAL,
];
export const IMAGE_TYPE_ID = 17;

type ComponentTypeLetterLogo = {
  type: 'letter',
  letter: string,
  color?: string,
  fontSize?: number,
}

type ComponentTypeMaterialIconLogo = {
  type: 'materialIcon',
  name: string,
  color: string,
}

type ComponentTypeSVGLogo = {
  type: 'svg',
  svg: string,
  border?: string,
  color: string,
}

export type ComponentTypeLogo = ComponentTypeLetterLogo | ComponentTypeMaterialIconLogo | ComponentTypeSVGLogo;

interface ShowFieldTest {
  (options: any): boolean;
}

type Label = string | ((obj: any) => string);

export interface AnyField {
  name: string,
  label: Label,
  defaultValue: any,
  tooltipName?: keyof typeof Messages,
  showIf?: ShowFieldTest,
}

export interface AnySelectFieldOption {
  label: string,
  value: string,
}

export interface SelectField<T extends AnySelectFieldOption = any> extends AnyField {
  type: 'select',
  options: T[] | ((obj: any) => T[]),
}

export interface TextField extends AnyField {
  type: 'field',
  defaultValue: string,
}

export interface Editor extends AnyField {
  type: 'editor',
  defaultValue: string,
}

export interface Code extends AnyField {
  type: 'code',
  defaultValue: string,
}

export interface Checkbox extends AnyField {
  type: 'checkbox',
  formValidation?: {
    formValidationType: 'required',
    formValidationId: string,
  },
  name: string,
  defaultValue: boolean,
}

export interface ImageInput extends AnyField {
  type: 'image',
  name: string,
  defaultValue: string,
}

export interface Color extends AnyField {
  type: 'color',
  name: string,
  defaultValue: string,
}

export interface DateTime extends AnyField {
  type: 'datetime',
  name: string,
  defaultValue: { epoch: number | null, offset: number },
}

interface Event {
  type: 'none' | 'formSubmit',
  when?: 'before' | 'after',
}

export interface StudioFile extends AnyField {
  type: 'studioFile',
  name: string,
  defaultValue: string,
}

export interface Events extends AnyField {
  type: 'events',
  name: string,
  defaultValue: Event[],
}

export interface Message {
  type: 'message',
  message: string,
  showIf?: ShowFieldTest,
  name: string,
}

export interface Namespace {
  type: 'namespace',
  showIf?: ShowFieldTest,
  name: string,
  fields: FieldSlot[],
}

export type FieldSlot = SelectField | TextField | Message | Code | Checkbox |
  ImageInput | Editor | Color | Events | StudioFile | DateTime | Namespace;

export type FixedPositions = 'top' | 'bottom';

export type ComponentListConfig = {
  name?: string,
  // logo: ComponentTypeLogo,
  key: string,
  validate: any[],
  default?: any[],
};

export type ComponentListConfigs = ComponentListConfig[];

export type ComponentTypeData = {
  id: number,
  name: string,
  componentType: 'campaign' | 'component' | 'wrapper' | 'form',
  ord: number,
  logo: ComponentTypeLogo,
  fixedOrd?: number,
  descriptionName?: keyof typeof Messages,
  componentLists?: ComponentListConfig[],
  options?: {
    fields?: FieldSlot[],
  },
  analyticsOptions?: {
    fields?: FieldSlot[],
  },
  styleOptions?: {
    fields?: FieldSlot[],
  },
};

type ComponentTypeDataEntries = {
  [id: number]: ComponentTypeData;
}

const isVariableChange = ({ action }: any) => action === 'changeVariable' || action === 'tagUserData';
const varOperationIs = (operation: string) => ({ varOperation }: { varOperation: string }) => operation === varOperation;

const actions: FieldSlot[] = [
    {
        type: 'select',
        name: 'action',
        label: 'Action',
        defaultValue: 'none',
        options: [
            { label: 'None', value: 'none' },
            { label: 'URL Link', value: 'link' },
            { label: 'Experience Link', value: 'experienceLink' },
            { label: __IS_V2__ ? 'Tag Variable' : 'Object Variable', value: 'tagUserData' },
            { label: 'Local Variable', value: 'changeVariable' },
            { label: 'Form Submit', value: 'formSubmit' },
        ],
    },
    {
        type: 'field',
        name: 'link',
        label: 'Link',
        defaultValue: '',
        showIf: ({ action }: any) => action === 'link',
    },
    {
        type: 'studioFile',
        name: 'studioFile',
        label: 'Studio Experience',
        defaultValue: '',
        showIf: ({ action }: any) => action === 'experienceLink',
    },
    {
        type: 'field',
        name: 'varName',
        label: 'Variable Name',
        defaultValue: '',
        showIf: isVariableChange,
    },
    {
        type: 'select',
        name: 'varOperation',
        label: 'Variable Operation',
        showIf: isVariableChange,
        defaultValue: 'set',
        options: [
            { label: 'Set', value: 'set' },
            { label: 'Increment', value: 'increment' },
            { label: 'Decrement', value: 'decrement' },
        ],
    },
    {
        type: 'field',
        name: 'opValue',
        label: 'Value',
        defaultValue: '',
        showIf: both(isVariableChange, varOperationIs('set')),
    },
    {
        type: 'field',
        name: 'opValue',
        label: 'Increment By',
        defaultValue: '1',
        showIf: both(isVariableChange, varOperationIs('increment')),
    },
    {
        type: 'field',
        name: 'opValue',
        label: 'Decrement By',
        defaultValue: '1',
        showIf: both(isVariableChange, varOperationIs('decrement')),
    },
];

const actionAnalytics: FieldSlot[] = [
    {
        type: 'namespace',
        name: 'actionAnalytics',
        fields: [
            {
                type: 'select',
                name: 'type',
                label: 'Action',
                defaultValue: 'default',
                options: [
                    { label: 'None', value: 'none' },
                    { label: 'Default', value: 'default' },
                    { label: 'Custom', value: 'custom' },
                ],
            },
            {
                type: 'namespace',
                name: 'customOptions',
                showIf: (os) => (os.actionAnalytics || {}).type === 'custom',
                fields: [
                    {
                        type: 'field',
                        name: 'eventCategory',
                        label: 'Event Category',
                        defaultValue: '',
                    },
                    {
                        type: 'field',
                        name: 'eventAction',
                        label: 'Event Action',
                        defaultValue: '',
                    },
                    {
                        type: 'field',
                        name: 'eventLabel',
                        label: 'Event Label',
                        defaultValue: '',
                    },
                ],
            },
        ],
    },
];

export const rootListConfig: ComponentListConfig = {
    key: 'items',
    validate: [
        { type: 'directChildrenType', blacklist: [13, 19, 20, 28, 29, 34] },
    ],
};

export const fixedListConfig: ComponentListConfig = {
    key: 'items',
    validate: [
        { type: 'directChildrenType', whitelist: [13, 34] },
    ],
};
export type ComponentTypeId = string & keyof ComponentTypeDataEntries;

const componentMetadata: FieldSlot = {
    type: 'namespace',
    name: 'metadata',
    fields: [
        {
            type: 'field',
            name: 'name',
            label: 'Component Name',
            defaultValue: '',
        },
    ],
};

const INPLACE_OPTIONS = [
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
    { label: 'GET', value: 'GET' },
];

const REDIRECT_OPTIONS = [
    { label: 'POST', value: 'POST' },
    { label: 'GET', value: 'GET' },
];

const componentTypeData: ComponentTypeDataEntries = {
    ...(!__IS_V2__ ? ({} as ComponentTypeDataEntries) : {
        [CAMPAIGN_A]: {
            id: CAMPAIGN_A,
            name: 'Campaign',
            componentType: 'campaign',
            ord: 2,
            logo: {
                type: 'svg',
                svg: '<svg height="100%" viewBox="0 0 25.2 26.4"><path d="M10.5 0h4.3l10.4 26.4h-5l-2.4-6.3H7.4L5 26.4H.1L10.5 0zM8.8 16.3h7.6l-3.8-10-3.8 10z" fill="#fff"/></svg>',
                color: '#378CC8',
            },
            descriptionName: 'componentDescriptionCampaign',
        },
        [CAMPAIGN_B]: {
            id: CAMPAIGN_B,
            name: 'Campaign',
            componentType: 'campaign',
            ord: 3,
            logo: {
                type: 'svg',
                svg: '<svg height="100%" viewBox="0 0 19 26.6"><path d="M.1.1h10c1.2 0 2.2.2 3.2.5s1.8.8 2.5 1.5c.7.6 1.2 1.4 1.6 2.2.4.9.6 1.8.6 2.8 0 1.4-.3 2.6-1 3.6s-1.6 1.8-2.7 2.3c1.5.5 2.6 1.3 3.4 2.4s1.2 2.4 1.2 3.9c0 1.1-.2 2.1-.6 2.9-.4.8-1 1.6-1.7 2.3-.7.6-1.6 1.1-2.6 1.5s-2.1.5-3.3.5H.1V.1zm4.8 11.1h4.3c1.2 0 2.1-.3 2.8-1 .7-.7 1.1-1.5 1.1-2.6S12.7 5.7 12 5c-.7-.7-1.7-1-2.8-1H4.9v7.2zm0 11.4h4.8c1.3 0 2.3-.4 3-1.1.8-.7 1.1-1.6 1.1-2.8 0-1.2-.4-2.1-1.1-2.8-.8-.7-1.8-1-3-1H4.9v7.7z" fill="#fff"/></svg>',
                color: '#378CC8',
            },
            descriptionName: 'componentDescriptionCampaign',
        },
        [CAMPAIGN_C]: {
            id: CAMPAIGN_C,
            name: 'Campaign',
            componentType: 'campaign',
            ord: 4,
            logo: {
                type: 'svg',
                color: '#378CC8',
                svg: '<svg height="100%" viewBox="0 0 20 26.6"><path d="M.1 13.2c0-1.9.3-3.7.9-5.3.6-1.6 1.5-3 2.6-4.2S6 1.6 7.6 1c1.5-.7 3.2-1 5-1 2.5 0 4.9.6 7.3 1.9v4.3c-1.1-.7-2.2-1.2-3.3-1.6-1.1-.4-2.2-.6-3.2-.6-1.2 0-2.4.2-3.4.7-1 .4-1.9 1.1-2.6 1.9-.7.8-1.3 1.8-1.7 2.9-.4 1.1-.6 2.4-.6 3.7s.2 2.6.6 3.7 1 2.1 1.7 2.9S9 21.2 10 21.7c1 .4 2.2.7 3.4.7 1 0 2.1-.2 3.2-.6 1.1-.4 2.2-.9 3.3-1.6v4.4c-2.4 1.2-4.8 1.9-7.3 1.9-1.8 0-3.5-.3-5-1s-2.9-1.6-4-2.7c-1.1-1.2-2-2.6-2.6-4.2-.6-1.7-.9-3.5-.9-5.4z" fill="#fff"/></svg>',
            },
            descriptionName: 'componentDescriptionCampaign',
        },
        [CAMPAIGN_D]: {
            id: CAMPAIGN_D,
            name: 'Campaign',
            componentType: 'campaign',
            ord: 5,
            logo: {
                type: 'svg',
                svg: '<svg height="100%" viewBox="0 0 23.4 27.3"><path d="M0 0h9.9c2 0 3.8.3 5.4 1s3.1 1.6 4.2 2.8c1.2 1.2 2.1 2.7 2.8 4.3.7 1.7 1 3.5 1 5.5s-.3 3.8-1 5.5-1.6 3.1-2.8 4.3c-1.2 1.2-2.6 2.2-4.2 2.8-1.6.7-3.5 1-5.4 1H0V0zm5.2 23.1h4.5c1.2 0 2.4-.2 3.4-.7s1.9-1.1 2.6-1.9 1.3-1.8 1.7-3c.4-1.2.6-2.4.6-3.8s-.2-2.7-.6-3.8c-.4-1.2-1-2.2-1.7-3-.7-.8-1.6-1.5-2.6-1.9-1-.5-2.2-.7-3.4-.7H5.2v18.8z" fill="#fff"/></svg>',
                color: '#378CC8',
            },
            descriptionName: 'componentDescriptionCampaign',
        },
    }),
    6: {
        id: 6,
        name: 'Weather',
        componentType: 'component',
        ord: 21,
        logo: {
            type: 'svg',
            svg: '<svg height="100%" viewBox="0 0 30.1 30"><path d="M7.3 8.3c.3.3.7.3 1 0s.3-.7 0-1L5.4 4.4c-.3-.3-.7-.3-1 0s-.3.7 0 1l2.9 2.9zM15 5.5c.4 0 .7-.3.7-.7V.7c0-.4-.3-.7-.7-.7s-.7.3-.7.7v4.1c0 .4.3.7.7.7zm7.7 2.8l2.9-2.9c.3-.3.3-.7 0-1s-.7-.3-1 0l-2.9 2.9c-.3.3-.3.7 0 1 .3.2.8.2 1 0zM5.5 15c0-.4-.3-.7-.7-.7H.7c-.4 0-.7.3-.7.7s.3.7.7.7h4.1c.4 0 .7-.3.7-.7zm23.8-.7h-4.1c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h4.1c.4 0 .7-.3.7-.7s-.3-.7-.7-.7zm-6.6 7.4c-.3-.3-.7-.3-1 0s-.3.7 0 1l2.9 2.9c.3.3.7.3 1 0s.3-.7 0-1l-2.9-2.9zM15 6.8c-4.5 0-8.2 3.7-8.2 8.2s3.7 8.2 8.2 8.2 8.2-3.7 8.2-8.2-3.7-8.2-8.2-8.2zm0 15c-3.8 0-6.8-3-6.8-6.8s3-6.8 6.8-6.8 6.8 3 6.8 6.8-3 6.8-6.8 6.8zm0 2.7c-.4 0-.7.3-.7.7v4.1c0 .4.3.7.7.7.4 0 .7-.3.7-.7v-4.1c0-.4-.3-.7-.7-.7zm-7.7-2.8l-2.9 2.9c-.3.3-.3.7 0 1s.7.3 1 0l2.9-2.9c.3-.3.3-.7 0-1-.3-.2-.8-.2-1 0z" fill="#fff"/></svg>',
            color: '#DDBD1B',
        },
        descriptionName: 'componentDescriptionWeather',
        options: {
            fields: [
                {
                    type: 'select',
                    name: 'format',
                    label: 'Temperature',
                    defaultValue: 'fahrenheit',
                    tooltipName: 'componentOptionsWeatherTemperature',
                    options: [
                        { label: 'Fahrenheit', value: 'fahrenheit' },
                        { label: 'Celsius', value: 'celsius' },
                    ],
                },
            ],
        },
    },
    7: {
        id: 7,
        name: 'YouTube',
        componentType: 'component',
        ord: 18,
        logo: {
            type: 'materialIcon',
            name: 'play_arrow',
            color: '#e62117',
        },
        descriptionName: 'componentDescriptionYouTube',
        options: {
            fields: [
                {
                    type: 'select',
                    name: 'type',
                    label: 'Source Type',
                    defaultValue: 'video',
                    tooltipName: 'componentOptionsYouTubeType',
                    options: [
                        { label: 'Video', value: 'video' },
                        { label: 'Playlist', value: 'playlist' },
                        { label: 'User', value: 'user' },
                    ],
                },
                {
                    type: 'field',
                    name: 'term',
                    label: 'Source ID',
                    tooltipName: 'componentOptionsYouTubeId',
                    defaultValue: '',
                },
            ],
        },
    },
    ...(!__IS_V2__ ? ({} as ComponentTypeDataEntries) : {
        [CAMPAIGN_SWIPER]: {
            id: CAMPAIGN_SWIPER,
            name: 'Swiper',
            componentType: 'campaign',
            ord: 6,
            logo: {
                type: 'svg',
                svg: '<svg height="100%" viewBox="0 0 30.2 26.6"><path d="M.1.1h14v4.1h-9v6.9h7.5v4H5.1v7.3h9.3v4.1H.1V.1zm15.3 11h6.8v4h-6.8v-4zm9.7-11h5v26.4h-5V.1z" fill="#fff"/></svg>',
                color: '#378CC8',
            },
            descriptionName: 'componentDescriptionSwiper',
        },
        [CAMPAIGN_INTERSTITIAL]: {
            id: CAMPAIGN_INTERSTITIAL,
            name: 'Interstitial',
            componentType: 'campaign',
            fixedOrd: 1,
            ord: 1,
            logo: {
                type: 'svg',
                svg: '<svg height="100%" viewBox="0 0 16.5 30"><circle fill="#fff" cx="8.3" cy="27.3" r=".7"/><path fill="#fff" d="M13.8 0h-11C1.3 0 .1 1.2.1 2.7v24.5c0 1.5 1.2 2.7 2.7 2.7h10.9c1.5 0 2.7-1.2 2.7-2.7V2.7c.1-1.5-1.1-2.7-2.6-2.7zm1.3 27.3c0 .8-.6 1.4-1.4 1.4H2.8c-.8 0-1.4-.6-1.4-1.4v-1.4H15l.1 1.4zm0-2.8H1.5v-19h13.6v19zm0-20.4H1.5V2.7c0-.8.6-1.4 1.4-1.4h10.9c.8 0 1.4.6 1.4 1.4v1.4h-.1z"/><path fill="#fff" d="M9 2H7.6c-.4 0-.7.3-.7.7s.3.7.7.7H9c.4 0 .7-.3.7-.7 0-.3-.3-.7-.7-.7zm3.6 8.9c.6 0 1.2-.5 1.4-1.1.2-.8-.3-1.7-1.1-1.9-.8-.2-1.7.3-1.9 1.1v.4H3.9v11.1h8.8l-.1-9.6zm-1.3 8.3H5.1v-8.5h6.2v8.5z"/></svg>',
                color: '#378CC8',
            },
            descriptionName: 'componentDescriptionInterstitial',
        },
    }),
    14: {
        id: 14,
        name: 'Snippet',
        componentType: 'component',
        ord: 23,
        logo: {
            type: 'svg',
            color: '#644076',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30.1 20.7" width="100%"><path d="M0 11.5V9.4l9.8-5.1v2.8L4 10.4l5.8 3.3v2.8l-9.8-5zM16.1.1h3l-5.2 20.6h-3L16.1.1zM30 9.3v2.1l-9.8 5.1v-2.8l5.8-3.3-5.8-3.3V4.3l9.8 5z" fill="#fff"/></svg>',
        },
        descriptionName: 'componentDescriptionSnippet',
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'code',
                    name: 'code',
                    label: 'Embedded Code',
                    tooltipName: 'componentOptionsSnippetHTML',
                    defaultValue: '',
                },
                {
                    type: 'checkbox',
                    name: 'rawHtml',
                    label: 'Raw HTML',
                    defaultValue: false,
                },
            ],
        },
    },
    15: {
        id: 15,
        name: 'Exclusive',
        componentType: 'wrapper',
        ord: 10,
        descriptionName: 'componentDescriptionExclusive',
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg width="100%" viewBox="0 0 33.1 30.1" width="100%"><g><path fill="#fff" d="M22,0c-5,0-9.3,3.4-10.6,8c-0.1,0-0.3,0-0.4,0C4.9,8,0,12.9,0,19s4.9,11,11,11c5,0,9.3-3.4,10.6-8c0.1,0,0.3,0,0.4,0c6.1,0,11-4.9,11-11S28.1,0,22,0z M11,28.8c-5.4,0-9.8-4.4-9.8-9.8S5.6,9.2,11,9.2s9.8,4.4,9.8,9.8C20.9,24.4,16.4,28.8,11,28.8z M22,20.9h-0.1c0.1-0.6,0.2-1.2,0.2-1.9c0-2.1-0.6-4-1.6-5.6l1.5-1.5l4.4,4.4c0.1,0.1,0.3,0.2,0.4,0.2c0.3,0,0.6-0.3,0.6-0.6c0-0.2-0.1-0.4-0.2-0.4l-4.4-4.4l4.4-4.4c0.1-0.1,0.2-0.3,0.2-0.4c0-0.3-0.3-0.6-0.6-0.6c-0.2,0-0.4,0.1-0.4,0.2l-4.5,4.4l-4.4-4.4c-0.1-0.1-0.3-0.2-0.4-0.2c-0.3,0-0.6,0.3-0.6,0.6c0,0.2,0.1,0.4,0.2,0.4l4.4,4.4l-1.4,1.4c-1.7-2.3-4.3-3.8-7.2-4.3c1.2-4,5-6.9,9.4-6.9c5.4,0,9.8,4.4,9.8,9.8C31.8,16.4,27.4,20.9,22,20.9z"/><path fill="#fff" d="M16.2,14c-0.1-0.1-0.3-0.1-0.5-0.1c-0.2,0.1-0.3,0.2-0.4,0.3l-5.7,9l-4.6-3c-0.1-0.1-0.3-0.1-0.5-0.1s-0.3,0.1-0.4,0.3C4,20.5,4,20.7,4,20.9c0.1,0.2,0.2,0.4,0.3,0.4l5.1,3.4c0.1,0.1,0.2,0.1,0.4,0.1h0.1c0.2-0.1,0.3-0.2,0.4-0.3l6-9.5l0.1-0.1c0.1-0.1,0.1-0.3,0.1-0.4C16.5,14.2,16.4,14.1,16.2,14z"/></g></svg>',
        },
        options: {
            fields: [
                {
                    type: 'select',
                    name: 'verified',
                    label: 'Verified',
                    defaultValue: true,
                    tooltipName: 'componentOptionsExclusiveVerified',
                    options: [
                        { label: 'true', value: true },
                        { label: 'false', value: false },
                    ],
                },
            ],
        },
        componentLists: [
            {
                key: 'passed',
                validate: [
                    { type: 'directChildrenType', blacklist: [15, 19, 20, 28, 29, 34] },
                ],
            },
        ],
    },
    16: {
        id: 16,
        name: 'Countdown',
        componentType: 'component',
        ord: 24,
        logo: {
            type: 'svg',
            color: '#644076',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30.1 30.1" width="100%"><path d="M14.9 7.7c-.4 0-.7-.3-.7-.7V1.4C7 1.8 1.3 7.6 1.3 15c0 7.5 6 13.6 13.6 13.6s13.6-6 13.6-13.6c0-7.3-5.7-13.2-12.9-13.6V7c0 .4-.3.7-.7.7z" fill="none"/><path fill="#fff" d="M12.3 13.8c-.5 1.1-.1 2.4.9 3.2 1.3 1 2.9.6 3.8-.5.8-1 .7-2.4 0-3.3l-.5-.5-.6-.3c-1-.4-2-.3-2.7.4L7.1 8.3c-.3-.2-.7-.2-1 .1-.2.3-.2.7.1 1l6.1 4.4zm1.4.1c.5-.6 1.3-.8 2-.3.6.5.8 1.3.3 2-.5.6-1.3.8-2 .3s-.8-1.3-.3-2z"/><path fill="#fff" d="M15.6 0h-1.4v7c0 .4.3.7.7.7s.7-.3.7-.7V1.4c7.2.4 12.9 6.3 12.9 13.6 0 7.6-6 13.6-13.6 13.6S1.3 22.5 1.3 15c0-3.1 1-6 2.8-8.2.5-.6-.5-1.6-1.1-.8-1.9 2.5-3 5.6-3 9 0 8.3 6.7 15 15 15s15-6.7 15-15C29.9 6.9 23.6.4 15.6 0z"/></svg>',
        },
        options: {
            fields: [
                {
                    type: 'datetime',
                    name: 'startDate',
                    label: 'Start Date',
                    tooltipName: 'componentOptionsTimestamp',
                    defaultValue: { offset: 0, epoch: null },
                },
                {
                    type: 'datetime',
                    name: 'endDate',
                    label: 'End Date',
                    tooltipName: 'componentOptionsTimestamp',
                    defaultValue: { offset: 0, epoch: null },
                },
                {
                    type: 'field',
                    name: 'text',
                    label: 'Text',
                    defaultValue: '',
                },
            ],
        },
        styleOptions: {
            fields: [
                {
                    type: 'color',
                    name: 'color',
                    label: 'Color',
                    defaultValue: '#2E71F0',
                },
            ],
        },
        descriptionName: 'componentDescriptionCountdown',
    },
    [IMAGE_TYPE_ID]: {
        id: IMAGE_TYPE_ID,
        name: 'Image',
        componentType: 'component',
        ord: 14,
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg width="100%" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30"><path d="M25.8 25c0-.1-.1-.2-.2-.2l-5.9-8.6c-.1-.3-.4-.4-.6-.4s-.4.1-.5.2l-2.8 2.8-3.6-5.3c-.1-.3-.4-.4-.6-.4s-.4.1-.6.3L4.2 25c-.1.1-.1.2-.1.4 0 .4.3.7.7.7h20.5c.4 0 .7-.3.7-.7l-.2-.4zm-19.9-.5l5.6-9.6L15 20c0 .1.1.2.1.2.1.1.3.2.5.2s.4-.1.5-.2l2.8-2.8 5 7.1h-18zM27.2 0H2.7C1.2 0 0 1.3 0 2.7v24.6C0 28.8 1.3 30 2.7 30h24.6c1.6 0 2.7-1.3 2.7-2.7V2.7C29.9 1.2 28.7 0 27.2 0zm1.3 27.2c0 .8-.6 1.4-1.4 1.4H2.7c-.8 0-1.4-.6-1.4-1.4V2.7c0-.8.6-1.4 1.4-1.4h24.6c.7 0 1.3.6 1.3 1.4l-.1 24.5zm-21-16.3c1.8 0 3.4-1.6 3.4-3.4S9.3 4.1 7.5 4.1 4.1 5.7 4.1 7.5s1.4 3.4 3.4 3.4zm0-5.5c1.2 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2c-.1-1.1.8-2 2-2z" fill="#fff"/></svg>',
        },
        descriptionName: 'componentDescriptionImage',
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'select',
                    name: 'source',
                    label: 'Source',
                    defaultValue: 'none',
                    options: [
                        { label: 'Upload', value: 'upload' },
                        { label: 'URL', value: 'url' },
                        { label: 'None', value: 'none' },
                    ],
                },
                {
                    type: 'image',
                    name: 'src',
                    label: 'Image File',
                    defaultValue: '',
                    tooltipName: 'componentOptionsImageSrc',
                    showIf: ({ source }: any) => source === 'upload',
                },
                {
                    type: 'field',
                    name: 'src',
                    label: 'URL',
                    defaultValue: '',
                    showIf: ({ source }: any) => source === 'url',
                },
                {
                    type: 'field',
                    name: 'alt',
                    label: 'Alt Text',
                    defaultValue: '',
                },
                ...actions,
            ],
        },
        analyticsOptions: {
            fields: actionAnalytics,
        },
    },
    18: {
        id: 18,
        name: 'Verified',
        componentType: 'component',
        ord: 22,
        logo: {
            type: 'svg',
            color: '#644076',
            svg: '<svg width="100%" viewBox="0 0 33.1 30.1" width="100%"><g><path fill="#fff" d="M22,0c-5,0-9.3,3.4-10.6,8c-0.1,0-0.3,0-0.4,0C4.9,8,0,12.9,0,19s4.9,11,11,11c5,0,9.3-3.4,10.6-8c0.1,0,0.3,0,0.4,0c6.1,0,11-4.9,11-11S28.1,0,22,0z M11,28.8c-5.4,0-9.8-4.4-9.8-9.8S5.6,9.2,11,9.2s9.8,4.4,9.8,9.8C20.9,24.4,16.4,28.8,11,28.8z M22,20.9h-0.1c0.1-0.6,0.2-1.2,0.2-1.9c0-2.1-0.6-4-1.6-5.6l1.5-1.5l4.4,4.4c0.1,0.1,0.3,0.2,0.4,0.2c0.3,0,0.6-0.3,0.6-0.6c0-0.2-0.1-0.4-0.2-0.4l-4.4-4.4l4.4-4.4c0.1-0.1,0.2-0.3,0.2-0.4c0-0.3-0.3-0.6-0.6-0.6c-0.2,0-0.4,0.1-0.4,0.2l-4.5,4.4l-4.4-4.4c-0.1-0.1-0.3-0.2-0.4-0.2c-0.3,0-0.6,0.3-0.6,0.6c0,0.2,0.1,0.4,0.2,0.4l4.4,4.4l-1.4,1.4c-1.7-2.3-4.3-3.8-7.2-4.3c1.2-4,5-6.9,9.4-6.9c5.4,0,9.8,4.4,9.8,9.8C31.8,16.4,27.4,20.9,22,20.9z"/><path fill="#fff" d="M16.2,14c-0.1-0.1-0.3-0.1-0.5-0.1c-0.2,0.1-0.3,0.2-0.4,0.3l-5.7,9l-4.6-3c-0.1-0.1-0.3-0.1-0.5-0.1s-0.3,0.1-0.4,0.3C4,20.5,4,20.7,4,20.9c0.1,0.2,0.2,0.4,0.3,0.4l5.1,3.4c0.1,0.1,0.2,0.1,0.4,0.1h0.1c0.2-0.1,0.3-0.2,0.4-0.3l6-9.5l0.1-0.1c0.1-0.1,0.1-0.3,0.1-0.4C16.5,14.2,16.4,14.1,16.2,14z"/></g></svg>',
        },
        descriptionName: 'componentDescriptionVerified',
    },
    19: {
        id: 19,
        name: 'Input',
        componentType: 'form',
        ord: 25,
        descriptionName: 'componentDescriptionInput',
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29.4 23.2" width="100%"><path fill="#fff" d="M22 14.5h5.4V8.7H22m-5.5 0H2v5.8h14.5"/><path fill="#fff" d="M2 14.5V8.7h14.4V6.9H2C1 6.9.2 7.7.2 8.7v5.8c0 1 .8 1.8 1.8 1.8h14.4v-1.8H2zm25.4-7.6H22v1.8h5.4v5.8H22v1.8h5.4c1 0 1.8-.8 1.8-1.8V8.7c0-1-.8-1.8-1.8-1.8z"/><path fill="#fff" d="M20.4 2.5h2.7c.6 0 1.1-.5 1.1-1.1S23.7.3 23.1.3h-7.7c-.6 0-1.1.5-1.1 1.1s.5 1.1 1.1 1.1h2.7v18.3h-2.7c-.6 0-1.1.5-1.1 1.1s.5 1.1 1.1 1.1h7.7c.6 0 1.1-.5 1.1-1.1s-.5-1.1-1.1-1.1h-2.7V2.5z"/></svg>',
        },
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'name',
                    label: 'Name',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'label',
                    label: 'Label',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'placeholder',
                    label: 'Placeholder',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'defaultValue',
                    label: 'Default Value',
                    defaultValue: '',
                },
                {
                    type: 'checkbox',
                    formValidation: {
                        formValidationType: 'required',
                        formValidationId: '1',
                    },
                    name: 'on',
                    label: 'Required',
                    defaultValue: false,
                },
            ],
        },
    },
    20: {
        id: 20,
        name: 'Checkbox',
        componentType: 'form',
        ord: 28,
        descriptionName: 'componentDescriptionCheckbox',
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25.2 26.4" width="100%"><path fill="#fff" d="M24.4 8.8c-.3 0-.6.3-.6.6v13.4c0 1.2-1 2.3-2.3 2.3h-18c-1.2 0-2.3-1-2.3-2.3v-18c0-1.2 1-2.3 2.3-2.3H17c.3 0 .6-.3.6-.6s-.3-.6-.6-.6H3.6C1.7 1.3.2 2.8.2 4.7v18c0 1.9 1.5 3.4 3.4 3.4h18c1.9 0 3.4-1.5 3.4-3.4V9.3c0-.3-.3-.5-.6-.5z"/><path fill="#fff" d="M5.4 11.5c-.1-.1-.2-.2-.4-.2-.3 0-.5.2-.5.5 0 .2.1.3.2.4l8.2 8.2c.1.1.2.2.4.2s.3-.1.4-.2L24.7 1c0-.1.2-.5-.2-.7-.4-.2-.8.3-.8.3L13 19l-7.6-7.5z"/></svg>',
        },
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'name',
                    label: 'Name',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'label',
                    label: 'Label',
                    defaultValue: '',
                },
                {
                    type: 'checkbox',
                    name: 'defaultValue',
                    label: 'Default Checked',
                    defaultValue: false,
                },
                {
                    type: 'checkbox',
                    formValidation: {
                        formValidationType: 'required',
                        formValidationId: '1',
                    },
                    name: 'on',
                    label: 'Required',
                    defaultValue: false,
                },
            ],
        },
    },
    21: {
        id: 21,
        name: 'Form',
        componentType: 'wrapper',
        ord: 9,
        descriptionName: 'componentDescriptionForm',
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30.1 29" width="100%"><path fill="#fff" d="M16.7 11.3c0 .4-.3.6-.6.6H4.9c-.4 0-.6-.3-.6-.6 0-.4.3-.6.6-.6h11.2c.4 0 .6.3.6.6zM4.9 7.8c-.4 0-.6-.3-.6-.6 0-.4.3-.6.6-.6h7.3c.4 0 .6.3.6.6 0 .4-.3.6-.6.6H4.9zm0 7.1h4c.4 0 .6.3.6.6 0 .4-.3.6-.6.6h-4c-.4 0-.6-.3-.6-.6-.1-.3.2-.6.6-.6z"/><path fill="#fff" d="M26.9 17.1c-.4 0-.6.3-.6.6V25c0 1.4-1.2 2.5-2.5 2.5h-20c-1.4 0-2.5-1.1-2.5-2.5V5c0-1.4 1.1-2.5 2.5-2.5h17.4c.4 0 .6-.3.6-.6 0-.4-.3-.6-.6-.6H3.8C1.7 1.3 0 3 0 5.1v20.1C0 27.3 1.7 29 3.8 29h20.1c2.1 0 3.8-1.7 3.8-3.8v-7.4c-.2-.4-.5-.7-.8-.7z"/><path fill="#fff" d="M29.8 2.1C29.3.5 27.6-.3 26 .2c-.8.3-1.3.8-1.7 1.5L16 19.1l.9 5.1 4.5-2.5 8.3-17.4c.3-.6.4-1.4.1-2.2zm-12 20.2l-.4-2.7 2.1-.7.7 2.1-2.4 1.3zm3.2-2.5l-.8-2.4-2.4.8 6.7-13.9 3.2 1.6L21 19.8zm7.6-16l-.5 1-3.2-1.6.5-1c.2-.4.6-.7 1-.9 1-.3 1.9.1 2.3 1.1.2.5.2 1-.1 1.4z"/></svg>',
        },
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'field',
                    name: 'title',
                    label: 'Title',
                    defaultValue: '',
                },
                {
                    type: 'select',
                    name: 'submissionType',
                    label: 'Type',
                    defaultValue: 'redirect',
                    options: [
                        { label: 'Form Submit', value: 'redirect' },
                        { label: 'Fetch/AJAX', value: 'inplace' },
                        { label: 'None', value: 'none' },
                    ],
                },
                {
                    type: 'field',
                    name: 'action',
                    label: 'Action',
                    tooltipName: 'componentDescriptionAction',
                    defaultValue: '',
                    showIf: compose(not, propEq('submissionType', 'none')),
                },
                {
                    type: 'select',
                    name: 'method',
                    label: 'Method',
                    tooltipName: 'componentOptionsFormMethod',
                    defaultValue: 'POST',
                    options: (obj) => (
                        obj.submissionType === 'inplace'
                            ? INPLACE_OPTIONS
                            : REDIRECT_OPTIONS
                    ),
                    showIf: compose(not, propEq('submissionType', 'none')),
                },
                {
                    type: 'select',
                    name: 'bodyType',
                    label: 'Body Type',
                    defaultValue: 'json',
                    options: [
                        { label: 'JSON', value: 'json' },
                    ],
                    showIf: propEq('submissionType', 'inplace'),
                },
                {
                    type: 'code',
                    name: 'bodyTemplate',
                    label: (obj) => (obj.method === 'GET' ? 'Query String' : 'Body Template'),
                    defaultValue: '{}',
                    showIf: propEq('submissionType', 'inplace'),
                },
            ],
        },
        styleOptions: {
            fields: [
                {
                    type: 'field',
                    name: 'inputWidth',
                    label: 'Input Width',
                    defaultValue: '100%',
                },
                {
                    type: 'field',
                    name: 'inputPadding',
                    label: 'Input Padding',
                    defaultValue: '6px 6px',
                },
                {
                    type: 'field',
                    name: 'inputMargins',
                    label: 'Input Margin',
                    defaultValue: '0px 0px 6px 0px',
                },
                {
                    type: 'field',
                    name: 'inputBorderWidth',
                    label: 'Input Border Width',
                    defaultValue: '1px',
                },
                {
                    type: 'field',
                    name: 'inputBorderRadius',
                    label: 'Input Border Radius',
                    defaultValue: '3px',
                },
                {
                    type: 'field',
                    name: 'inputFontSize',
                    label: 'Input Font Size',
                    defaultValue: '14px',
                },
                {
                    type: 'select',
                    name: 'inputFontWeight',
                    label: 'Input Font Weight',
                    defaultValue: '400',
                    options: [
                        { label: 'Light', value: '300' },
                        { label: 'Normal', value: '400' },
                        { label: 'Bold', value: '700' },
                    ],
                },
                {
                    type: 'select',
                    name: 'inputTextAlign',
                    label: 'Input Text Align',
                    defaultValue: 'left',
                    options: [
                        { label: 'Left', value: 'left' },
                        { label: 'Right', value: 'right' },
                        { label: 'Center', value: 'center' },
                        { label: 'Justify', value: 'justify' },
                    ],
                },
                {
                    type: 'field',
                    name: 'labelMargins',
                    label: 'Label Margin',
                    defaultValue: '0px 0px 6px 0px',
                },
                {
                    type: 'field',
                    name: 'labelFontSize',
                    label: 'Label Font Size',
                    defaultValue: '14px',
                },
                {
                    type: 'select',
                    name: 'labelFontWeight',
                    label: 'Label Font Weight',
                    defaultValue: '400',
                    options: [
                        { label: 'Light', value: '300' },
                        { label: 'Normal', value: '400' },
                        { label: 'Bold', value: '700' },
                    ],
                },
                {
                    type: 'select',
                    name: 'labelTextAlign',
                    label: 'Label Text Align',
                    defaultValue: 'left',
                    options: [
                        { label: 'Left', value: 'left' },
                        { label: 'Right', value: 'right' },
                        { label: 'Center', value: 'center' },
                        { label: 'Justify', value: 'justify' },
                    ],
                },
                {
                    type: 'color',
                    name: 'inputTextColor',
                    label: 'Input Text Color',
                    defaultValue: '#000000',
                },
                {
                    type: 'color',
                    name: 'labelTextColor',
                    label: 'Label Text Color',
                    defaultValue: '#6b6b6b',
                },
                {
                    type: 'color',
                    name: 'inputBackgroundColor',
                    label: 'Input Background Color',
                    defaultValue: '#ffffff',
                },
                {
                    type: 'color',
                    name: 'inputBorderColor',
                    label: 'Input Border Color',
                    defaultValue: 'rgb(221, 221, 221)',
                },
            ],
        },
        componentLists: [
            {
                key: 'body',
                validate: [
                    { type: 'directChildrenType', whitelist: [19, 20, 22, 26, 28, 29, 31, 17] },
                ],
            },
        ],
    },
    22: {
        id: 22,
        name: 'Text',
        componentType: 'component',
        ord: 16,
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26.8 26.6" width="100%"><path d="M1.4 12.1h16.8c.7 0 1.2-.5 1.2-1.2V8.5c0-.7-.5-1.2-1.2-1.2H1.4C.7 7.3.2 7.8.2 8.5v2.4c0 .7.5 1.2 1.2 1.2zm0-3.6h16.8v2.4H1.4V8.5zm24-8.4h-24C.7.1.2.6.2 1.3v2.4c0 .7.5 1.2 1.2 1.2h24c.7 0 1.2-.5 1.2-1.2V1.3c0-.7-.5-1.2-1.2-1.2zm0 3.6h-24V1.3h24v2.4zm-7.2 18H1.4c-.7 0-1.2.5-1.2 1.2v2.4c0 .7.5 1.2 1.2 1.2h16.8c.7 0 1.2-.5 1.2-1.2v-2.4c0-.6-.5-1.2-1.2-1.2zm0 3.6H1.4v-2.4h16.8v2.4zm7.2-10.8h-24c-.7 0-1.2.5-1.2 1.2v2.4c0 .7.5 1.2 1.2 1.2h24c.7 0 1.2-.5 1.2-1.2v-2.4c0-.7-.5-1.2-1.2-1.2zm0 3.6h-24v-2.4h24v2.4z" fill="#fff"/></svg>',
        },
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'editor',
                    name: 'converted_markdown',
                    label: '',
                    defaultValue: '',
                },
            ],
        },
        descriptionName: 'componentDescriptionEditor',
    },
    23: {
        id: 23,
        name: 'Schedule',
        componentType: 'wrapper',
        ord: 11,
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 22.8" width="100%"><path d="M15.3 9.3h-2.2v2.2h2.2V9.3zm4.5 6.2H22v-2.2h-2.2v2.2zm-1.1-2.3h-2.2v2.2h2.2v-2.2zM22 9.3h-5.6v2.2H22V9.3zM9.7 19.4h2.2v-2.2H9.7v2.2zm-6.8 0h2.3v-2.2H2.9v2.2zm3.4 0h2.2v-2.2H6.3v2.2zm9-6.2h-2.2v2.2h2.2v-2.2zm-3.3 0H2.9v2.2H12v-2.2zM5.2 9.3H2.9v2.2h2.3V9.3zm17.4-8h-2.2V.6c0-.3-.3-.6-.6-.6s-.6.3-.6.6v.7H5.7V.6c0-.3-.2-.6-.5-.6s-.6.3-.6.6v.7H2.4C1.2 1.3.2 2.3.2 3.6v17c0 1.2 1 2.2 2.2 2.2h20.2c1.2 0 2.2-1 2.2-2.2v-17c.1-1.3-.9-2.3-2.2-2.3zm1.1 19.2c0 .6-.5 1.1-1.1 1.1H2.4c-.6 0-1.1-.5-1.1-1.1V7h22.5v13.5zm0-14.6H1.2V3.5c0-.6.5-1.1 1.1-1.1h2.3v.7c0 .3.3.6.6.6s.6-.3.6-.6v-.7h13.5v.7c0 .3.3.6.6.6s.6-.3.6-.6v-.7h2.2c.6 0 1.1.5 1.1 1.1v2.4zM11.9 9.3H9.7v2.2h2.2V9.3zm-3.3 0H6.3v2.2h2.2V9.3zm6.7 7.9h-2.2v2.2h2.2v-2.2z" fill="#fff"/></svg>',
        },
        descriptionName: 'componentDescriptionSchedule',
        options: {
            fields: [
                {
                    type: 'datetime',
                    name: 'startDate',
                    label: 'Start Date',
                    tooltipName: 'componentOptionsTimestamp',
                    defaultValue: { offset: 0, epoch: null },
                },
                {
                    type: 'datetime',
                    name: 'endDate',
                    label: 'End Date',
                    tooltipName: 'componentOptionsTimestamp',
                    defaultValue: { offset: 0, epoch: null },
                },
            ],
        },
        componentLists: [
            {
                key: 'valid',
                validate: [
                    { type: 'directChildrenType', blacklist: [23, 19, 20, 28, 29, 34] },
                ],
            },
        ],
    },
    24: {
        id: 24,
        name: 'Gallery',
        componentType: 'wrapper',
        ord: 8,
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg width="100%" viewBox="0 0 30.2 30"><path d="M11 16.4H2.8c-1.6 0-2.7 1.3-2.7 2.7v8.2c0 1.5 1.3 2.7 2.7 2.7H11c1.6 0 2.7-1.3 2.7-2.7v-8.2c0-1.5-1.2-2.7-2.7-2.7zm1.4 10.9c0 .8-.6 1.4-1.4 1.4H2.8c-.8 0-1.4-.6-1.4-1.4v-8.2c0-.8.6-1.4 1.4-1.4H11c.8 0 1.4.6 1.4 1.4v8.2zM27.4 0h-8.2c-1.6 0-2.7 1.3-2.7 2.7v8.2c0 1.6 1.3 2.7 2.7 2.7h8.2c1.6 0 2.7-1.3 2.7-2.7V2.7c0-1.5-1.2-2.7-2.7-2.7zm1.3 10.9c0 .8-.6 1.4-1.4 1.4h-8.2c-.8 0-1.4-.6-1.4-1.4V2.7c0-.8.6-1.4 1.4-1.4h8.2c.8 0 1.4.6 1.4 1.4v8.2zM11 0H2.8C1.3 0 .1 1.3.1 2.7v8.2c0 1.6 1.3 2.7 2.7 2.7H11c1.6 0 2.7-1.3 2.7-2.7V2.7C13.7 1.2 12.5 0 11 0zm1.4 10.9c0 .8-.6 1.4-1.4 1.4H2.8c-.8 0-1.4-.6-1.4-1.4V2.7c0-.8.6-1.4 1.4-1.4H11c.8 0 1.4.6 1.4 1.4v8.2zm15 5.5h-8.2c-1.6 0-2.7 1.3-2.7 2.7v8.2c0 1.6 1.3 2.7 2.7 2.7h8.2c1.6 0 2.7-1.3 2.7-2.7v-8.2c0-1.5-1.2-2.7-2.7-2.7zm1.3 10.9c0 .8-.6 1.4-1.4 1.4h-8.2c-.8 0-1.4-.6-1.4-1.4v-8.2c0-.8.6-1.4 1.4-1.4h8.2c.8 0 1.4.6 1.4 1.4v8.2z" fill="#fff"/></svg>',
        },
        descriptionName: 'componentDescriptionGallery',
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'select',
                    name: 'type',
                    label: 'Type',
                    defaultValue: 'grid',
                    tooltipName: 'componentOptionsGalleryType',
                    options: [
                        { label: 'Grid', value: 'grid' },
                        { label: 'Swiper', value: 'swiper' },
                    ],
                },
                {
                    type: 'select',
                    name: 'rowSize',
                    showIf: ({ type }: any) => type === 'grid',
                    label: 'Row Size',
                    defaultValue: '3',
                    options: [
                        { label: '1', value: '1' },
                        { label: '2', value: '2' },
                        { label: '3', value: '3' },
                        { label: '4', value: '4' },
                        { label: '5', value: '5' },
                        { label: '6', value: '6' },
                    ],
                },
            ],
        },
        componentLists: [
            {
                key: 'content',
                validate: [
                    { type: 'directChildrenType', whitelist: [17] },
                ],
            },
        ],
    },
    25: {
        id: 25,
        name: 'Event',
        componentType: 'wrapper',
        ord: 13,
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg width="100%" height="100%" viewBox="0 0 26.8 24.9"><g><polygon fill="#ffffff" points="1.4,10.1 0.2,8.9 0.2,10.7 1.4,11.9 6.7,17.2 8,18.4 9.8,18.4 8.6,17.2   "/><polygon fill="#ffffff" points="1.4,13.7 0.2,12.5 0.2,14.3 1.4,15.5 3.1,17.2 4.3,18.4 6.2,18.4 4.9,17.2   "/><path fill="#ffffff" d="M1.4,17.1l-1.2-1.2v1.3c0,0.7,0.5,1.2,1.2,1.2h1.3L1.4,17.1L1.4,17.1z"/><path fill="#ffffff" d="M25.4,6.5H8.3L2,0.3C1.8,0,1.3,0,1.1,0.3C0.8,0.6,0.8,1,1.1,1.2l5.4,5.3l1.2,1.2l9.5,9.4l1.2,1.2l6.3,6.2c0.1,0.1,0.3,0.2,0.5,0.2c0.2,0,0.3-0.1,0.5-0.2c0.3-0.3,0.2-0.7,0-0.9l-5.4-5.3h5.1c0.7,0,1.2-0.5,1.2-1.2V7.7C26.6,7,26.1,6.5,25.4,6.5z M25.4,17.2h-6.3L9.6,7.7h15.8V17.2z"/><polygon fill="#ffffff" points="6,7.7 4.8,6.5 2.9,6.5 4.1,7.7 13.7,17.2 14.9,18.4 16.7,18.4 15.5,17.2   "/><path    fill="#ffffff" d="M2.5,7.7L1.3,6.6c-0.5,0-0.9,0.3-1,0.8l1.1,1.1l8.8,8.7l1.2,1.2h1.9L12,17.2L2.5,7.7z"/></g></svg>',
        },
        descriptionName: 'componentDescriptionEvent',
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'events',
                    name: 'events',
                    label: 'Event',
                    defaultValue: [{ type: 'none' }],
                },
            ],
        },
        componentLists: [
            {
                key: 'content',
                validate: [
                    { type: 'directChildrenType', blacklist: [19, 20, 28, 29, 34] },
                ],
            },
        ],
    },
    26: {
        id: 26,
        name: 'Action',
        componentType: 'component',
        ord: 17,
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg width="100%" height="100%" viewBox="0 0 23.4 24.6"><path fill="#ffffff" d="M13.4,20.4c0.8-0.1,1.5-0.3,2.2-0.6c0,0,0.4-0.2,0.7-0.2c0.3,0.1,0.5,0.5,0.4,0.8c-0.1,0.2-0.2,0.3-0.3,0.4c0,0,0,0,0,0c-0.9,0.4-1.9,0.8-2.9,0.9c-5.2,0.7-10-3.1-10.6-8.2S5.8,3.6,11,3c2.7-0.3,5.3,0.4,7.3,2.2c0,0,0.1,0.1,0.1,0.1c0,0,0.1,0,0.1,0.1l0,0.7c0,0.1,0,0.1-0.1,0.1l-0.7,0c0,0-0.1-0.1-0.1-0.1c-1.7-1.4-4-2.1-6.2-1.9c-4.5,0.6-7.6,4.6-7,9.1S9,21,13.4,20.4z M23.2,18c0.2-0.3,0-0.7-0.3-0.9c-0.3-0.2-0.7,0-0.9,0.3c-1.6,3.1-4.8,5.2-8.2,5.7c-6,0.8-11.4-3.5-12.2-9.4S5.1,2.2,11,1.5c2.7-0.3,5.5,0.4,7.7,2c0.3,0.2,0.8,0.7,1,0.9l0.8-1L21,8.1l-4.7,0.5L17,7.8c-0.2-0.2-0.4-0.4-0.6-0.6c-1.2-1-3-1.5-4.7-1.3C8.1,6.4,5.6,9.7,6,13.2c0.2,1.5,0.9,2.8,1.9,3.8c0.3,0.3,0.7,0.6,1.1,0.9c0.5,0.3,1.4,0.9,1.9,0.5c0.3-0.2,0.3-0.7,0.1-1c-0.1-0.1-0.2-0.1-0.2-0.2C9,16.6,7.6,15,7.4,13.1C7,10.3,9,7.7,11.8,7.3C13,7.2,14.1,7.5,15,8.1L14.3,9c-0.1,0.1-0.2,0.3-0.1,0.5c0,0.1,0,0.2,0.1,0.2c0.1,0.2,0.4,0.4,0.7,0.3l6.9-0.8c0.4,0,0.6-0.4,0.6-0.7l-0.8-6.9c-0.1-0.2-0.2-0.5-0.5-0.6c-0.3-0.1-0.5,0-0.7,0.2l-1,1.2c-2.5-1.8-5.6-2.7-8.7-2.3C4.1,1-0.7,7.2,0.2,13.8s7,11.4,13.7,10.6C17.8,23.9,21.3,21.5,23.2,18z"/></svg>',
        },
        descriptionName: 'componentDescriptionAction',
        options: {
            fields: [
                componentMetadata,
                ...actions,
            ],
        },
        analyticsOptions: {
            fields: actionAnalytics,
        },
    },
    27: {
        id: 27,
        name: 'Section',
        componentType: 'wrapper',
        ord: 7,
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg width="100%" height="100%" viewBox="0 0 26.8 16.3"><path fill="#ffffff" d="M26.2,0.1H0.6c-0.3,0-0.5,0.2-0.5,0.5v15.2c0,0.3,0.2,0.5,0.5,0.5h0.2h1.9h23.6c0.3,0,0.5-0.2,0.5-0.5V0.6C26.7,0.3,26.5,0.1,26.2,0.1z M25.6,15.2L1.2,15.1l0-13.9h24.3V15.2z"/><g><circle fill="#ffffff" cx="5.7" cy="9.8" r="2.1"/><circle fill="#ffffff" cx="11.6" cy="9.1" r="2.9"/><circle fill="#ffffff" class="st0" cx="19.4" cy="8.2" r="3.8"/></g></svg>',
        },
        descriptionName: 'componentDescriptionSection',
        options: {
            fields: [
                componentMetadata,
            ],
        },
        styleOptions: {
            fields: [
                {
                    type: 'field',
                    name: 'height',
                    label: 'Height',
                    defaultValue: 'auto',
                },
                {
                    type: 'field',
                    name: 'width',
                    label: 'Width',
                    defaultValue: 'auto',
                },
                {
                    type: 'field',
                    name: 'padding',
                    label: 'Padding',
                    defaultValue: '20px',
                },
                {
                    type: 'field',
                    name: 'margin',
                    label: 'Margin',
                    defaultValue: '8px 0px',
                },
                {
                    type: 'field',
                    name: 'borderWidth',
                    label: 'Border Width',
                    defaultValue: '1px',
                },
                {
                    type: 'field',
                    name: 'borderRadius',
                    label: 'Border Radius',
                    defaultValue: '2px',
                },
                {
                    type: 'field',
                    name: 'fontSize',
                    label: 'Font Size',
                    defaultValue: '12px',
                },
                {
                    type: 'select',
                    name: 'fontWeight',
                    label: 'Font Weight',
                    defaultValue: '400',
                    options: [
                        { label: 'Light', value: '300' },
                        { label: 'Normal', value: '400' },
                        { label: 'Bold', value: '700' },
                    ],
                },
                {
                    type: 'select',
                    name: 'textAlign',
                    label: 'Text Align',
                    defaultValue: 'left',
                    options: [
                        { label: 'Left', value: 'left' },
                        { label: 'Right', value: 'right' },
                        { label: 'Center', value: 'center' },
                        { label: 'Justify', value: 'justify' },
                    ],
                },
                {
                    type: 'color',
                    name: 'textColor',
                    label: 'Text Color',
                    defaultValue: '#000000',
                },
                {
                    type: 'color',
                    name: 'backgroundColor',
                    label: 'Background Color',
                    defaultValue: '#ffffff',
                },
                {
                    type: 'color',
                    name: 'borderColor',
                    label: 'Border Color',
                    defaultValue: '#dddddd',
                },
                {
                    type: 'select',
                    name: 'display',
                    label: 'Display',
                    defaultValue: 'block',
                    options: [
                        { label: 'Block', value: 'block' },
                        { label: 'Flex', value: 'flex' },
                    ],
                },
                {
                    type: 'namespace',
                    name: 'flexOptions',
                    showIf: (os) => os.display === 'flex',
                    fields: [
                        {
                            type: 'select',
                            name: 'flex-direction',
                            label: 'Flex Direction',
                            defaultValue: 'row',
                            options: [
                                { label: 'Row', value: 'row' },
                                { label: 'Row Reverse', value: 'row-reverse' },
                                { label: 'Column', value: 'column' },
                                { label: 'Column Reverse', value: 'column-reverse' },
                            ],
                        },
                        {
                            type: 'select',
                            name: 'flex-wrap',
                            label: 'Flex Wrap',
                            defaultValue: 'nowrap',
                            options: [
                                { label: 'No Wrap', value: 'nowrap' },
                                { label: 'Wrap', value: 'wrap' },
                                { label: 'Wrap Reverse', value: 'wrap-reverse' },
                            ],
                        },
                        {
                            type: 'select',
                            name: 'justify-content',
                            label: 'Justify Content',
                            defaultValue: 'normal',
                            options: [
                                { label: 'Normal', value: 'normal' },
                                { label: 'Flex Start', value: 'flex-start' },
                                { label: 'Flex End', value: 'flex-end' },
                                { label: 'Left', value: 'left' },
                                { label: 'Right', value: 'right' },
                                { label: 'Center', value: 'center' },
                                { label: 'Space Between', value: 'space-between' },
                                { label: 'Space Around', value: 'space-around' },
                                { label: 'Space Evenly', value: 'space-evenly' },
                            ],
                        },
                        {
                            type: 'select',
                            name: 'align-items',
                            label: 'Align Items',
                            defaultValue: 'normal',
                            options: [
                                { label: 'Normal', value: 'normal' },
                                { label: 'Stretch', value: 'stretch' },
                                { label: 'Flex Start', value: 'flex-start' },
                                { label: 'Flex End', value: 'flex-end' },
                                { label: 'Center', value: 'center' },
                                { label: 'Baseline', value: 'baseline' },
                            ],
                        },
                        {
                            type: 'select',
                            name: 'align-content',
                            label: 'Align Content',
                            defaultValue: 'normal',
                            options: [
                                { label: 'Normal', value: 'normal' },
                                { label: 'Stretch', value: 'stretch' },
                                { label: 'Flex Start', value: 'flex-start' },
                                { label: 'Flex End', value: 'flex-end' },
                                { label: 'Center', value: 'center' },
                                { label: 'Space Between', value: 'space-between' },
                                { label: 'Space Around', value: 'space-around' },
                                { label: 'Space Evenly', value: 'space-evenly' },
                            ],
                        },
                    ],
                },
                {
                    type: 'select',
                    name: 'flexChild',
                    label: 'Flex Child',
                    defaultValue: 'false',
                    options: [
                        { label: 'True', value: 'true' },
                        { label: 'False', value: 'false' },
                    ],
                },
                {
                    type: 'namespace',
                    name: 'flexChildOptions',
                    showIf: (os) => os.flexChild === 'true',
                    fields: [
                        {
                            type: 'field',
                            name: 'flex-basis',
                            label: 'Flex Basis',
                            defaultValue: 'auto',
                        },
                        {
                            type: 'select',
                            name: 'align-self',
                            label: 'Align Self',
                            defaultValue: 'auto',
                            options: [
                                { label: 'Auto', value: 'auto' },
                                { label: 'Self Start', value: 'self-start' },
                                { label: 'Self End', value: 'self-end' },
                                { label: 'Flex Start', value: 'flex-start' },
                                { label: 'Flex End', value: 'flex-end' },
                                { label: 'Center', value: 'center' },
                                { label: 'Stretch', value: 'stretch' },
                            ],
                        },
                        {
                            type: 'field',
                            name: 'flex-grow',
                            label: 'Flex Grow',
                            defaultValue: '0',
                        },
                        {
                            type: 'field',
                            name: 'flex-shrink',
                            label: 'Flex Shrink',
                            defaultValue: '1',
                        },
                        {
                            type: 'field',
                            name: 'order',
                            label: 'Order',
                            defaultValue: '0',
                        },
                    ],
                },
            ],
        },
        componentLists: [
            {
                key: 'content',
                validate: [
                    { type: 'directChildrenType', blacklist: [19, 20, 28, 29, 34] },
                ],
            },
        ],
    },
    28: {
        id: 28,
        name: 'Hidden Input',
        componentType: 'form',
        ord: 26,
        descriptionName: 'componentDescriptionHiddenInput',
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29.4 23.2" width="100%"><path fill="#fff" d="M22 14.5h5.4V8.7H22m-5.5 0H2v5.8h14.5"/><path fill="#fff" d="M2 14.5V8.7h14.4V6.9H2C1 6.9.2 7.7.2 8.7v5.8c0 1 .8 1.8 1.8 1.8h14.4v-1.8H2zm25.4-7.6H22v1.8h5.4v5.8H22v1.8h5.4c1 0 1.8-.8 1.8-1.8V8.7c0-1-.8-1.8-1.8-1.8z"/><path fill="#fff" d="M20.4 2.5h2.7c.6 0 1.1-.5 1.1-1.1S23.7.3 23.1.3h-7.7c-.6 0-1.1.5-1.1 1.1s.5 1.1 1.1 1.1h2.7v18.3h-2.7c-.6 0-1.1.5-1.1 1.1s.5 1.1 1.1 1.1h7.7c.6 0 1.1-.5 1.1-1.1s-.5-1.1-1.1-1.1h-2.7V2.5z"/></svg>',
        },
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'name',
                    label: 'Name',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'value',
                    label: 'Value',
                    defaultValue: '',
                },
            ],
        },
    },
    29: {
        id: 29,
        name: 'Select',
        componentType: 'form',
        ord: 27,
        descriptionName: 'componentDescriptionSelect',
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg width="100%" height="100%" viewBox="0 0 26.8 8"><path fill="#ffffff" d="M25.4,0.1L25.4,0.1h-24h0c-0.7,0-1.2,0.5-1.2,1.2v5.4c0,0.7,0.5,1.2,1.2,1.2h0h24h0c0.7,0,1.2-0.5,1.2-1.2V1.3C26.6,0.6,26.1,0.1,25.4,0.1z M1.4,1.3h17.9v5.4H1.4V1.3z M20.4,6.7V1.3h5v5.4H20.4z M3.5,4.5V3.4h8.8v1.1H3.5z M22.9,5.1c-0.1,0-0.2,0-0.3-0.1l-1.4-1.2c-0.2-0.2-0.2-0.5,0-0.6c0.2-0.2,0.5-0.2,0.6,0l1.1,1l1.1-1c0.2-0.2,0.5-0.2,0.6,0c0.2,0.2,0.2,0.5,0,0.6L23.2,5C23.1,5.1,23,5.1,22.9,5.1z"/></svg>',
        },
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'name',
                    label: 'Name',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'label',
                    label: 'Label',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'defaultValue',
                    label: 'Default Value',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'optionLabels',
                    label: 'Option Labels',
                    defaultValue: '',
                },
                {
                    type: 'field',
                    name: 'optionValues',
                    label: 'Option Values',
                    defaultValue: '',
                },
                {
                    type: 'checkbox',
                    formValidation: {
                        formValidationType: 'required',
                        formValidationId: '1',
                    },
                    name: 'on',
                    label: 'Required',
                    defaultValue: false,
                },
            ],
        },
    },
    30: {
        id: 30,
        name: 'Delay',
        componentType: 'wrapper',
        ord: 12,
        descriptionName: 'componentDescriptionDelay',
        logo: {
            type: 'svg',
            color: '#E24632',
            svg: '<svg width="100%" height="100%" viewBox="0 0 30.1 30.1"><g><g><g><g><path fill="#ffffff" d="M12.4,13.8c-0.5,1.1-0.1,2.4,0.9,3.2c1.3,1,2.9,0.6,3.8-0.5c0.8-1,0.7-2.4,0-3.3l-0.5-0.5L16,12.4c-1-0.4-2-0.3-2.7,0.4L7.2,8.3c-0.3-0.2-0.7-0.2-1,0.1C6,8.7,6,9.1,6.3,9.4L12.4,13.8z M13.8,13.9c0.5-0.6,1.3-0.8,2-0.3c0.6,0.5,0.8,1.3,0.3,2c-0.5,0.6-1.3,0.8-2,0.3S13.3,14.6,13.8,13.9z"/><path fill="#ffffff" d="M15.7,0c-0.2,0-0.5,0-0.7,0s-0.5,0-0.7,0l0,1.4l0,5.6c0,0.4,0.3,0.7,0.7,0.7s0.7-0.3,0.7-0.7l0-5.6c7.2,0.4,12.9,6.3,12.9,13.6c0,7.6-6,13.6-13.6,13.6S1.4,22.5,1.4,15c0-3.1,1-6,2.8-8.2C4.7,6.2,3.7,5.2,3.1,6c-1.9,2.5-3,5.6-3,9c0,8.3,6.7,15,15,15s15-6.7,15-15C30,7,23.7,0.5,15.7,0z"/></g></g></g></g><path fill="#ffffff" d="M16.8,2.5l-1.1,8.9c1.8,0.1,2.5,1.8,2.5,1.8L26,9C26,9,23.5,4.3,16.8,2.5z"/></svg>',
        },
        options: {
            fields: [
                {
                    type: 'select',
                    name: 'show',
                    label: 'Show',
                    defaultValue: 'after',
                    options: [
                        { label: 'Before', value: 'before' },
                        { label: 'After', value: 'after' },
                    ],
                },
                {
                    type: 'field',
                    name: 'delay',
                    label: 'Delay',
                    defaultValue: '3',
                },
            ],
        },
        componentLists: [
            {
                key: 'content',
                validate: [
                    { type: 'directChildrenType', blacklist: [19, 20, 28, 29, 34] },
                ],
            },
        ],
    },
    31: {
        id: 31,
        name: 'Button',
        componentType: 'component',
        ord: 15,
        logo: {
            type: 'svg',
            color: '#8DC15E',
            svg: '<svg width="100%" height="100%" viewBox="0 0 24.8 15.5"><path fill="#ffffff" d="M24.6,1.3v5.4c0,0.7-0.5,1.2-1.2,1.2h-7.1V6.7h7.1V1.3h-22v5.4h8c0,0.4,0,0.8,0,1.2H1.4c-0.7,0-1.2-0.5-1.2-1.2V1.3c0-0.7,0.5-1.2,1.2-1.2h22C24.1,0.1,24.6,0.6,24.6,1.3z M10.8,10.5c0,0,0-0.1-0.1-0.1c-0.1-0.4-0.3-0.7-0.4-1.1C10.2,9,10,8.8,9.6,8.8C9.2,8.9,9,9,9,9.3c0,0.1,0,0.3,0,0.5c0.2,0.9,0.5,1.7,0.7,2.6c0.1,0.2,0.1,0.4,0.3,0.6c0.2,0.2,0.3,0.4,0.5,0.6c0.5,0.5,0.8,1.1,1.1,1.7c0,0.1,0.1,0.1,0.2,0.1c1.1,0,2.2,0,3.3,0c0.1,0,0.1,0,0.1-0.1c0.2-0.8,0.4-1.6,0.5-2.4c0.1-0.5,0.2-0.9,0.2-1.4c0-0.8,0-1.7,0-2.5c0-0.2-0.1-0.4-0.2-0.5c-0.1-0.1-0.2-0.1-0.3-0.2c-0.2,0-0.4,0-0.5,0.2c0-0.4,0-0.7-0.4-0.8c-0.3-0.1-0.6,0-0.9,0.3c0-0.2,0-0.4,0-0.5c0-0.3-0.1-0.5-0.4-0.6c-0.3-0.1-0.6,0-0.8,0.2c0,0,0,0.1-0.1,0.1c0,0,0,0,0,0c0-0.1,0-0.3,0-0.4c0-0.3,0-0.7,0-1c0-0.2-0.1-0.4-0.3-0.5c-0.5-0.2-1,0.1-1,0.6c0,1.5,0,3,0,4.5C10.9,10.4,10.9,10.5,10.8,10.5C10.9,10.5,10.9,10.5,10.8,10.5z"/></svg>',
        },
        descriptionName: 'componentDescriptionButton',
        options: {
            fields: [
                componentMetadata,
                {
                    type: 'field',
                    name: 'buttonText',
                    label: 'Button Text',
                    defaultValue: 'Submit',
                },
                ...actions,
            ],
        },
        analyticsOptions: {
            fields: actionAnalytics,
        },
        styleOptions: {
            fields: [
                {
                    type: 'select',
                    name: 'buttonAlign',
                    label: 'Align',
                    defaultValue: 'left',
                    options: [
                        { label: 'Left', value: 'left' },
                        { label: 'Center', value: 'center' },
                        { label: 'Right', value: 'right' },
                    ],
                },
                {
                    type: 'field',
                    name: 'height',
                    label: 'Height',
                    defaultValue: 'auto',
                },
                {
                    type: 'field',
                    name: 'width',
                    label: 'Width',
                    defaultValue: 'auto',
                },
                {
                    type: 'field',
                    name: 'padding',
                    label: 'Padding',
                    defaultValue: '6px 18px',
                },
                {
                    type: 'field',
                    name: 'margin',
                    label: 'Margin',
                    defaultValue: '12px 0px 0px 0px',
                },
                {
                    type: 'field',
                    name: 'borderWidth',
                    label: 'Border Width',
                    defaultValue: '0px',
                },
                {
                    type: 'field',
                    name: 'borderRadius',
                    label: 'Border Radius',
                    defaultValue: '2px',
                },
                {
                    type: 'field',
                    name: 'fontSize',
                    label: 'Font Size',
                    defaultValue: '14px',
                },
                {
                    type: 'select',
                    name: 'fontWeight',
                    label: 'Font Weight',
                    defaultValue: '400',
                    options: [
                        { label: 'Light', value: '300' },
                        { label: 'Normal', value: '400' },
                        { label: 'Bold', value: '700' },
                    ],
                },
                {
                    type: 'select',
                    name: 'textAlign',
                    label: 'Text Align',
                    defaultValue: 'center',
                    options: [
                        { label: 'Left', value: 'left' },
                        { label: 'Right', value: 'right' },
                        { label: 'Center', value: 'center' },
                        { label: 'Justify', value: 'justify' },
                    ],
                },
                {
                    type: 'color',
                    name: 'textColor',
                    label: 'Text Color',
                    defaultValue: '#ffffff',
                },
                {
                    type: 'color',
                    name: 'backgroundColor',
                    label: 'Button Color',
                    defaultValue: '#2e71f0',
                },
                {
                    type: 'color',
                    name: 'borderColor',
                    label: 'Border Color',
                    defaultValue: '#dddddd',
                },
            ],
        },
    },
    32: {
        id: 32,
        name: 'Instagram Embed',
        componentType: 'component',
        ord: 19,
        descriptionName: 'componentDescriptionInstagramEmbed',
        logo: {
            type: 'svg',
            svg: '<svg width="100%" viewBox="0 0 24 24"><path fill="#ffffff" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>',
            color: 'radial-gradient(circle at 33% 100%, #FED373 4%, #F15245 30%, #D92E7F 62%, #9B36B7 85%, #515ECF)',
        },
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'url',
                    label: 'URL',
                    defaultValue: '',
                },
            ],
        },
    },
    33: {
        id: 33,
        name: 'Twitter',
        componentType: 'component',
        ord: 20,
        descriptionName: 'componentDescriptionTwitter',
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'url',
                    label: 'URL',
                    defaultValue: '',
                },
            ],
        },
        styleOptions: {
            fields: [
                {
                    type: 'select',
                    name: 'theme',
                    label: 'Theme',
                    defaultValue: 'light',
                    options: [
                        { label: 'Light', value: 'light' },
                        { label: 'Dark', value: 'dark' },
                    ],
                },
                {
                    type: 'field',
                    name: 'maxHeight',
                    label: 'Max Height',
                    defaultValue: 'auto',
                },
                {
                    type: 'field',
                    name: 'maxWidth',
                    label: 'Max Width',
                    defaultValue: 'auto',
                },
            ],
        },
        logo: {
            type: 'svg',
            svg: '<svg width="100%" viewBox="0 0 300.00006 244.18703"><g style="" transform="translate(-539.18 -568.86)"><path d="m633.9 812.04c112.46 0 173.96-93.168 173.96-173.96 0-2.6463-0.0539-5.2806-0.1726-7.903 11.938-8.6302 22.314-19.4 30.498-31.66-10.955 4.8694-22.744 8.1474-35.111 9.6255 12.623-7.5693 22.314-19.543 26.886-33.817-11.813 7.0031-24.895 12.093-38.824 14.841-11.157-11.884-27.041-19.317-44.629-19.317-33.764 0-61.144 27.381-61.144 61.132 0 4.7978 0.5364 9.4646 1.5854 13.941-50.815-2.5569-95.874-26.886-126.03-63.88-5.2508 9.0354-8.2785 19.531-8.2785 30.73 0 21.212 10.794 39.938 27.208 50.893-10.031-0.30992-19.454-3.0635-27.69-7.6468-0.009 0.25652-0.009 0.50661-0.009 0.78077 0 29.61 21.075 54.332 49.051 59.934-5.1376 1.4006-10.543 2.1516-16.122 2.1516-3.9336 0-7.766-0.38716-11.491-1.1026 7.7838 24.293 30.355 41.971 57.115 42.465-20.926 16.402-47.287 26.171-75.937 26.171-4.929 0-9.7983-0.28036-14.584-0.84634 27.059 17.344 59.189 27.464 93.722 27.464" fill="#1da1f2"/></g></svg>',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            color: '#ffffff',
        },
    },
    34: {
        id: 34,
        name: 'Cookie Banner',
        componentType: 'component',
        fixedOrd: 2,
        ord: 21,
        descriptionName: 'componentDescriptionCookieBanner',
        options: {
            fields: [
                {
                    type: 'field',
                    name: 'ppUrl',
                    label: 'Privacy Policy URL',
                    defaultValue: '',
                },
            ],
        },
        styleOptions: {
            fields: [
                {
                    type: 'select',
                    name: 'theme',
                    label: 'Theme',
                    defaultValue: 'light',
                    options: [
                        { label: 'Light', value: 'light' },
                        { label: 'Dark', value: 'dark' },
                    ],
                },
            ],
        },
        logo: {
            type: 'svg',
            svg: '<svg height="100%" viewBox="0 0 30.1 30.1" style="enable-background:new 0 0 30.1 30.1;"><style type="text/css">.st0{fill:#FFFFFF;}</style><g id="Layer_1"><g><path class="st0" d="M25.6,15.9c-0.2-0.8-0.5-1.5-0.9-2.2c-0.6-1-1.3-2-2.1-2.8c0,0-0.1-0.1-0.1-0.2c0-0.9,0-1.7,0-2.6c0-0.4,0-0.8-0.1-1.2c-0.1-1-0.5-2-1.1-2.9c-0.6-0.9-1.3-1.7-2.3-2.3c-0.4-0.3-0.8-0.5-1.3-0.7c-1.3-0.5-2.6-0.6-4-0.4C12,1,10.5,1.9,9.3,3.3c-1.2,1.4-1.7,3-1.7,4.8c0,0.9,0,1.7,0,2.6c0,0.1,0,0.2-0.1,0.3c-1.3,1.2-2.2,2.8-2.8,4.5c-0.4,1.1-0.6,2.2-0.6,3.3c0,1.3,0.2,2.5,0.7,3.7c0.1,0.3,0.4,0.4,0.7,0.3c0.3-0.1,0.4-0.4,0.3-0.7c-0.3-0.7-0.5-1.4-0.5-2.2c-0.1-1.4,0.1-2.8,0.5-4.2c0.3-0.8,0.7-1.6,1.1-2.3c0.8-1.3,1.9-2.3,3.2-3.1c0.6-0.3,1.2-0.6,1.8-0.8c1.4-0.5,2.9-0.6,4.4-0.5c1.1,0.1,2.1,0.4,3.1,0.8c0.6,0.3,1.2,0.6,1.7,1c1.2,0.9,2.2,2,2.9,3.3c0.3,0.5,0.5,1.1,0.7,1.7c0.3,0.9,0.5,1.9,0.5,2.9c0,0.8-0.2,1.6-0.5,2.3c-0.6,1.2-1.6,1.9-2.9,2.1c-0.4,0.1-0.7,0.1-1.1,0.1c-0.2,0-0.4,0-0.5,0.2c-0.1,0.2-0.1,0.3-0.1,0.5c0.1,0.2,0.3,0.3,0.5,0.3c0.6,0,1.3,0,1.9-0.2c1.1-0.3,2-0.9,2.7-1.8c0.7-1,1-2.1,1.1-3.3C26.1,17.9,25.9,16.9,25.6,15.9z M19.2,8.7c-2.7-1-5.5-1-8.2,0c-0.1-1.7,0.4-3.1,1.8-4c1.4-0.9,2.9-1,4.4-0.1C18.7,5.5,19.3,7,19.2,8.7z M21.5,10c-0.2-0.1-0.3-0.2-0.5-0.3c-0.2-0.1-0.4-0.3-0.6-0.4c-0.1-0.1-0.1-0.1-0.1-0.3c0-0.6,0-1.2-0.1-1.7c-0.1-1-0.6-1.9-1.3-2.7c-1-1.1-2.4-1.7-3.9-1.7c-0.7,0-1.3,0.2-2,0.4c-1.3,0.6-2.1,1.5-2.7,2.7C10.1,6.7,9.9,7.3,9.9,8c0,0.4,0,0.7,0,1.1c0,0.1,0,0.1-0.1,0.2C9.4,9.5,9,9.7,8.6,10c0-0.1,0-0.2,0-0.3c0-0.5,0-1.1,0-1.6c0-1.9,0.7-3.5,2.1-4.7c0.8-0.8,1.8-1.3,2.9-1.5C14.9,1.5,16,1.6,17.2,2c1.3,0.4,2.3,1.2,3.1,2.3c0.6,0.9,1,1.8,1.2,2.8c0,0.3,0.1,0.5,0.1,0.8c0,0.6,0,1.3,0,1.9C21.5,9.9,21.5,9.9,21.5,10z"/><path class="st0" d="M13.9,28.5c-0.2-0.1-0.4-0.3-0.7-0.4c-1.1-0.7-2-1.7-2.7-2.8c-0.5-0.8-0.9-1.7-1.2-2.6c-0.6-1.8-0.8-3.8-0.3-5.6c0.2-0.8,0.6-1.5,1.1-2.1c1.5-1.8,3.4-2.5,5.6-2.3c0.9,0.1,1.7,0.4,2.5,0.8c0.3,0.2,0.6,0.1,0.7-0.2c0.2-0.2,0.1-0.6-0.2-0.7c-0.2-0.1-0.4-0.2-0.6-0.3c-0.7-0.4-1.5-0.6-2.3-0.7c-0.7-0.1-1.3-0.1-1.9,0c-0.9,0.1-1.8,0.4-2.7,0.9C10.4,13.2,9.6,14,9,14.9c-0.8,1.2-1.1,2.5-1.2,3.9c-0.1,1.1,0,2.2,0.3,3.3c0.2,1.1,0.6,2.1,1.1,3.1c0.5,1,1.1,1.8,1.9,2.6c0.7,0.7,1.4,1.2,2.3,1.7c0.3,0.1,0.6,0.1,0.7-0.2C14.3,29,14.2,28.7,13.9,28.5z"/><path class="st0" d="M20.4,19c-0.1-0.3-0.2-0.6-0.2-1c-0.1-0.6-0.2-1.2-0.5-1.8c-0.3-0.7-0.7-1.3-1.3-1.8c-0.7-0.5-1.5-0.8-2.3-0.9c-1.6-0.3-3.3,0.2-4.6,1.2c-1,0.9-1.5,2-1.8,3.3c-0.1,0.3,0.1,0.7,0.5,0.7c0.2,0,0.5-0.1,0.5-0.4c0.1-0.4,0.2-0.9,0.3-1.3c0.7-1.5,2-2.4,3.6-2.6c1-0.1,2.1,0.1,3,0.7c0.5,0.4,0.9,0.9,1.1,1.5c0.2,0.5,0.3,1,0.4,1.5c0.1,0.5,0.2,1,0.5,1.5c0.2,0.5,0.9,0.8,1.4,0.8c0.7-0.1,1-0.6,1.2-1.3c0.1-0.5,0.1-1,0-1.5c-0.2-1.2-0.6-2.3-1.4-3.3c-0.2-0.3-0.5-0.3-0.7-0.1c-0.3,0.2-0.3,0.5-0.1,0.8c0.4,0.5,0.7,1.1,0.9,1.7c0.2,0.7,0.3,1.4,0.3,2.1c0,0.3,0,0.6-0.4,0.7C20.5,19.6,20.4,19.1,20.4,19z"/><path class="st0" d="M17.2,28.3c-0.2-0.1-0.3-0.1-0.5-0.2c-1.1-0.5-2.1-1.2-3-2c-1.3-1.2-2.3-2.8-2.8-4.5c-0.1-0.4-0.2-0.8-0.3-1.3c-0.1-0.3-0.2-0.6-0.5-0.6c-0.5,0-0.5,0.7-0.4,1.1c0.3,1.7,1,3.2,2,4.6c1.3,1.7,3,3,5,3.9c0.3,0.1,0.6,0,0.7-0.3C17.7,28.7,17.5,28.5,17.2,28.3z"/><path class="st0" d="M8.8,25.9c-0.9-1.6-1.5-3.3-1.7-5.1C7,20.1,7,19.4,7,18.7c0.1-0.9,0.2-1.8,0.5-2.6c0.3-0.8,0.7-1.6,1.3-2.3c0.2-0.2,0.2-0.6-0.1-0.7c-0.2-0.2-0.6-0.1-0.7,0.1c-1.1,1.4-1.8,3.2-2,5c0,0,0,0.1,0,0.1c-0.1,1.3,0,2.6,0.2,3.8c0.2,1.2,0.6,2.3,1.2,3.4c0.3,0.7,0.7,1.3,1.1,2c0.2,0.3,0.5,0.3,0.7,0.1c0.2-0.2,0.3-0.5,0.1-0.7C9.2,26.5,8.9,26.2,8.8,25.9z"/><path class="st0" d="M22.9,25c-0.8,0.2-1.6,0.3-2.4,0.2c-0.9-0.1-1.8-0.3-2.6-0.7c-0.4-0.2-0.8-0.5-1.2-0.8c0,0-0.2-0.2-0.2-0.2c-0.8-0.8-1.4-1.8-1.8-2.9c-0.2-0.5-0.3-1.1-0.2-1.6c0-0.2,0.1-0.4,0.2-0.6c0.2-0.2,0.4-0.2,0.7-0.1c0.3,0.1,0.4,0.3,0.4,0.6c0,0.3,0,0.7,0.1,1c0.1,0.7,0.3,1.4,0.7,2c0.4,0.7,0.9,1.2,1.5,1.6c0.3,0.2,0.5,0.1,0.7-0.1c0.2-0.2,0.1-0.5-0.1-0.7c-0.6-0.4-1-0.9-1.3-1.5c-0.3-0.5-0.5-1.1-0.5-1.7c0-0.4,0-0.9-0.2-1.3c-0.1-0.2-0.2-0.3-0.3-0.5c-0.3-0.3-0.6-0.5-1-0.5c-0.3-0.1-0.5,0-0.7,0.1c-0.1,0-0.2,0.1-0.3,0.1c-0.2,0.2-0.4,0.3-0.6,0.6c-0.1,0.2-0.2,0.5-0.3,0.7c-0.1,0.7,0.1,1.5,0.3,2.2c0.4,1.2,1,2.3,1.9,3.2c0.1,0.1,0.4,0.3,0.4,0.4c1.1,0.9,2.4,1.5,3.8,1.7c1.1,0.2,2.2,0.1,3.3-0.2c0.3-0.1,0.5-0.3,0.4-0.6C23.5,25.1,23.2,25,22.9,25z"/><path class="st0" d="M20.5,27.1c-0.7,0-1.3-0.1-2-0.3c-1.7-0.5-3.1-1.5-4.2-2.8c-0.6-0.8-1.1-1.7-1.4-2.6c-0.3-0.9-0.5-1.8-0.4-2.7c0.1-0.5,0.2-1,0.5-1.4c0.6-0.8,1.4-1.1,2.4-0.9c0.9,0.2,1.5,0.7,1.9,1.5c0.2,0.4,0.2,0.9,0.3,1.4c0.1,0.5,0.1,0.9,0.4,1.4c0.2,0.4,0.5,0.7,0.8,1c0,0,0.1,0.1,0.2,0.1c1,0.6,2,0.7,3.1,0.3c1.1-0.5,1.8-1.3,2-2.5c0.2-0.9,0.1-1.7-0.1-2.5c-0.2-1-0.6-1.9-1.1-2.7C22.2,13,21.2,12,20,11.2c-0.4-0.2-0.8-0.5-1.2-0.7c-0.7-0.3-1.5-0.6-2.3-0.7c-1-0.2-2-0.2-3,0c-1.3,0.2-2.5,0.7-3.6,1.5c-0.3,0.2-0.3,0.5-0.2,0.7c0.2,0.2,0.5,0.3,0.7,0.1c0.1-0.1,0.2-0.1,0.3-0.2c1.5-0.9,3.1-1.3,4.9-1.2c1.2,0.1,2.4,0.4,3.5,1c0.8,0.4,1.4,1,2,1.6c0.8,1,1.4,2,1.8,3.2c0.2,0.7,0.4,1.4,0.3,2.1c0,0.5-0.1,1-0.4,1.5c-0.6,0.8-1.4,1.2-2.5,1c-0.7-0.1-1.3-0.6-1.5-1.3c-0.1-0.5-0.2-0.9-0.2-1.4c-0.1-0.8-0.4-1.5-0.9-2.1c-0.8-0.9-1.8-1.2-3-1.1c-1.1,0.1-2,0.7-2.6,1.7c-0.9,1.5-0.5,3.4,0,4.9c0.5,1.4,1.3,2.6,2.4,3.6c1.2,1.1,2.6,2,4.2,2.3c0.6,0.1,1.2,0.2,1.8,0.2c0.2,0,0.4-0.1,0.5-0.3C21.1,27.4,20.9,27.1,20.5,27.1z"/></g></g></svg>',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            color: '#644076',
        },
    },
};

export const infoForComponentTypeId = (typeId: number): ComponentTypeData => componentTypeData[typeId] ?? null;

export default componentTypeData;
